import React from 'react';
import {
  ContentfulArticle,
  ContentfulCategoryLandingPage,
  ContentfulCompetition,
  ContentfulContentHubHomepage,
  ContentfulGenericPage,
  ContentfulHeroCta,
  ContentfulSubCategoryPage,
} from '@jbhi-fi/apis/src/contentful/types';
import { CardCoverImageProps, CardTagProps } from '../../Card';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import { ThemeTransformer } from './ThemeTransformer';
import { AssetTransformer } from './AssetTransformer';
import { breakpointSizePx, palette } from '@jbhi-fi/lanyard-ui/themes';
import { CardGridCardProps } from '../../Card/CardGrid/CardGrid';
import { ArticleTransformer } from './ArticleTransformer';
import { CompetitionTransformer } from './CompetitionTransformer';
import { HeroCtaTransformer } from './HeroCtaTransformer';
import { GenericPageTransformer } from './GenericPageTransformer';
import { MiniCardProps } from '../../MiniCard/MiniCard';

type CardCompatibleEntry = ContentfulArticle | ContentfulCompetition | ContentfulHeroCta | ContentfulGenericPage;

export abstract class CardTransformer {
  static get LOADING_CARD_PROPS() {
    return { cover: <></>, isLoading: true };
  }

  static isCompetition = (entry?: CardCompatibleEntry): entry is ContentfulCompetition =>
    entry?.sys?.contentType?.sys?.id === 'competition';

  static isHeroCta = (entry?: CardCompatibleEntry): entry is ContentfulHeroCta =>
    entry?.sys?.contentType?.sys?.id === 'heroCta';

  static isArticle = (entry?: CardCompatibleEntry): entry is ContentfulArticle =>
    entry?.sys?.contentType?.sys?.id === 'article';

  static isGenericPage = (entry?: CardCompatibleEntry): entry is ContentfulGenericPage =>
    entry?.sys?.contentType?.sys?.id === 'genericPage';

  static isSubCategory = (
    entry?: ContentfulSubCategoryPage | ContentfulCategoryLandingPage | ContentfulContentHubHomepage
  ): entry is ContentfulSubCategoryPage => entry?.sys?.contentType?.sys?.id === 'subLandingPage';

  static isLoading = (entry?: CardCompatibleEntry) => {
    if (CardTransformer.isHeroCta(entry)) {
      return !entry?.fields?.theme?.fields;
    }
    if (CardTransformer.isGenericPage(entry)) {
      if (entry?.fields?.heroLogo) return !entry?.fields?.heroLogo?.fields;
      else return false;
    }

    if (CardTransformer.isArticle(entry)) {
      const articleImage = entry?.fields?.heroImage ?? entry?.fields?.heroVideo?.fields?.landscapeCoverImage;
      return articleImage ? !articleImage?.fields : false;
    }

    return !entry?.fields?.heroImage?.fields;
  };

  static toTagProps = (
    entry?: CardCompatibleEntry,
    { includeSubCategoryHref }: { includeSubCategoryHref?: boolean } = {}
  ): CardTagProps | undefined => {
    if (CardTransformer.isHeroCta(entry)) {
      return undefined;
    }

    if (CardTransformer.isCompetition(entry) && entry?.fields?.stackMemberExclusive) {
      return {
        label: 'MEMBER EXCLUSIVE',
        icon: <Icon name="StackIllustrated" />,
        palette: {
          background: palette.primary.main,
          text: palette.primary.dark,
        },
      };
    }

    if (
      CardTransformer.isSubCategory(entry?.fields?.parentPage) &&
      (entry?.fields?.parentPage?.fields?.categoryPage?.fields?.heroTheme?.fields ||
        entry?.fields?.parentPage?.fields?.themeColour)
    ) {
      const categoryPalette = ThemeTransformer.toContentThemePalette(
        entry?.fields?.parentPage?.fields?.categoryPage?.fields?.heroTheme
      );
      const subCategoryPalette = ThemeTransformer.toContentThemePalette(entry?.fields.parentPage?.fields?.themeColour);

      return {
        label: entry?.fields?.parentPage?.fields?.heading,
        href: includeSubCategoryHref ? entry?.fields?.parentPage?.fields?.urlSlug : undefined,
        palette: Object.keys(subCategoryPalette)?.length ? subCategoryPalette : categoryPalette,
      };
    }

    return undefined;
  };

  static toHref = (entry?: CardCompatibleEntry): string => {
    if (CardTransformer.isHeroCta(entry)) {
      return entry?.fields?.url ?? '';
    }
    if (CardTransformer.isArticle(entry) || CardTransformer.isCompetition(entry)) {
      return entry?.fields?.parentPage?.fields?.categoryPage?.fields
        ? `/blogs/${entry?.fields?.parentPage?.fields?.categoryPage?.fields?.urlSlug}/${entry?.fields?.urlSlug}`
        : '';
    }

    if (CardTransformer.isGenericPage(entry)) {
      const subDirectory = entry?.fields?.subDirectory;
      return CardTransformer.isSubCategory(entry?.fields?.parentPage)
        ? subDirectory === 'Pages'
          ? `/pages/${entry?.fields?.urlSlug}`
          : `/blogs/${entry?.fields?.parentPage?.fields?.categoryPage?.fields?.urlSlug}/${entry?.fields?.urlSlug}`
        : '';
    }
    return '';
  };

  private static readonly DEFAULT_IMAGE_SIZES = `(max-width: ${breakpointSizePx.md}px) 100vw, 350px`;

  static toCardCoverImageProps = (entry?: CardCompatibleEntry): CardCoverImageProps | undefined => {
    if (CardTransformer.isLoading(entry)) {
      return undefined;
    }
    if (CardTransformer.isHeroCta(entry)) {
      const baseProps = AssetTransformer.toContentfulImageProps(entry?.fields?.image);
      return baseProps ? { ...baseProps, sizes: '160px' } : baseProps;
    }
    if (CardTransformer.isGenericPage(entry)) {
      const baseProps = AssetTransformer.toContentfulImageProps(entry?.fields?.heroLogo);
      return baseProps ? { ...baseProps, sizes: '160px' } : baseProps;
    }

    if (CardTransformer.isArticle(entry)) {
      const coverImage = entry?.fields?.heroImage ?? entry?.fields?.heroVideo?.fields?.landscapeCoverImage;
      const baseProps = AssetTransformer.toContentfulImageProps(coverImage);
      return baseProps
        ? { ...baseProps, sizes: CardTransformer.DEFAULT_IMAGE_SIZES, isVideoCard: !!entry?.fields?.heroVideo?.fields }
        : baseProps;
    }

    const baseProps = AssetTransformer.toContentfulImageProps(entry?.fields?.heroImage);

    return baseProps
      ? {
          ...baseProps,
          sizes: CardTransformer.DEFAULT_IMAGE_SIZES,
          isVideoCard: CardTransformer.isArticle(entry) && !!entry?.fields?.heroVideo?.fields,
        }
      : baseProps;
  };

  static toMiniCardProps = (entry: CardCompatibleEntry): MiniCardProps | undefined => {
    if (CardTransformer.isCompetition(entry)) {
      return CompetitionTransformer.toMiniCardProps(entry);
    }
    if (CardTransformer.isArticle(entry)) {
      return ArticleTransformer.toMiniCardProps(entry);
    }
    if (CardTransformer.isGenericPage(entry)) {
      return GenericPageTransformer.toMiniCardProps(entry);
    }
    return undefined;
  };

  static toCardProps = (entry?: CardCompatibleEntry): CardGridCardProps => {
    if (!entry || CardTransformer.isLoading(entry)) {
      return CardTransformer.LOADING_CARD_PROPS;
    }
    if (CardTransformer.isCompetition(entry)) {
      return CompetitionTransformer.toCardProps(entry);
    }
    if (CardTransformer.isArticle(entry)) {
      return ArticleTransformer.toCardProps(entry);
    }
    if (CardTransformer.isHeroCta(entry)) {
      return HeroCtaTransformer.toCardProps(entry);
    }
    if (CardTransformer.isGenericPage(entry)) {
      return GenericPageTransformer.toCardProps(entry);
    }

    const _exhaustiveCheck: never = entry;
    return _exhaustiveCheck;
  };
}
